import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QrCode, MapPin, Plus, Trash2, Loader2, Printer, RefreshCw, Crosshair, Tablet, Copy, ExternalLink, KeyRound } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

/** Postes de pointage (QR tournant 20 min) : création, liste, affichage/impression du QR. */
export function TerminalsManager({ terminals, onChanged }) {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [form, setForm] = useState({ name: "", site: "", lat: "", lng: "", geofence_radius_m: 100 });
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [shown, setShown] = useState(null);
  const [kiosk, setKiosk] = useState(null);

  const locate = () => {
    if (!navigator.geolocation) return toast.error("Géolocalisation indisponible");
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm((f) => ({ ...f, lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) })),
      () => toast.error("Position introuvable"),
      { timeout: 8000 },
    );
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Nom du poste requis");
    setSaving(true);
    try {
      await api.post("/tracking/terminals", { ...form, lat: parseFloat(form.lat) || 0, lng: parseFloat(form.lng) || 0, geofence_radius_m: parseInt(form.geofence_radius_m, 10) || 100 });
      toast.success("Poste créé — affichez son QR sur site");
      setForm({ name: "", site: "", lat: "", lng: "", geofence_radius_m: 100 });
      setShowForm(false);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Création impossible");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Supprimer le poste « ${t.name} » ? Les QR affichés ne fonctionneront plus.`)) return;
    try { await api.delete(`/tracking/terminals/${t.terminal_id}`); toast.success("Poste supprimé"); onChanged?.(); } catch (err) { toast.error(err.response?.data?.detail || "Suppression impossible"); }
  };

  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 mt-6" data-testid="terminals-manager">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <QrCode className="w-4 h-4 text-cyan-400" />
          <h3 className="text-sm font-bold text-white">Postes de pointage (QR tournant 20 min)</h3>
        </div>
        <Button size="sm" onClick={() => setShowForm((v) => !v)} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] text-xs font-bold h-8" data-testid="terminal-add-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Nouveau poste
        </Button>
      </div>
      <p className="text-[11px] text-slate-500 mb-3">Affichez le QR d'un poste sur une TV ou une tablette : les salariés le scannent depuis l'app Blitzz Salarié, même sans réseau. Le code change toutes les 20 min, la présence est vérifiée à la synchronisation et le pointage est validé automatiquement.</p>

      {showForm && (
        <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-4 bg-[#0b0811] border border-white/5 rounded-lg p-3" data-testid="terminal-form">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom (ex. Atelier Lyon)" className="md:col-span-2 bg-[#14101c] border-white/10 text-white text-xs" data-testid="terminal-name-input" />
          <Input value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} placeholder="Site / adresse" className="md:col-span-2 bg-[#14101c] border-white/10 text-white text-xs" data-testid="terminal-site-input" />
          <Input value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder="Latitude" className="bg-[#14101c] border-white/10 text-white text-xs" data-testid="terminal-lat-input" />
          <Input value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} placeholder="Longitude" className="bg-[#14101c] border-white/10 text-white text-xs" data-testid="terminal-lng-input" />
          <div className="md:col-span-6 flex items-center gap-2 flex-wrap">
            <label className="text-[11px] text-slate-400 flex items-center gap-1">Rayon
              <Input type="number" min="20" value={form.geofence_radius_m} onChange={(e) => setForm({ ...form, geofence_radius_m: e.target.value })} className="w-20 h-7 bg-[#14101c] border-white/10 text-white text-xs" data-testid="terminal-radius-input" /> m
            </label>
            <Button type="button" size="sm" variant="ghost" onClick={locate} className="text-xs text-slate-300 h-7" data-testid="terminal-locate-btn"><Crosshair className="w-3.5 h-3.5 mr-1" /> Ma position</Button>
            <div className="flex-1" />
            <Button type="submit" size="sm" disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs h-8" data-testid="terminal-save-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Créer le poste"}
            </Button>
          </div>
        </form>
      )}

      {terminals.length === 0 ? (
        <p className="text-xs text-slate-500 italic" data-testid="terminals-empty">Aucun poste. Créez-en un pour générer son QR.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {terminals.map((t) => (
            <div key={t.terminal_id} className="bg-[#0b0811] border border-white/5 rounded-lg p-3 flex flex-col gap-2" data-testid={`terminal-${t.terminal_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-bold text-white">{t.name || t.label}</div>
                  <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {t.lat ? `${t.lat.toFixed?.(4)}, ${t.lng?.toFixed?.(4)}` : "Sans GPS"} · {t.geofence_radius_m || t.radius_meters || 100} m
                  </div>
                </div>
                <button onClick={() => remove(t)} className="text-slate-500 hover:text-rose-400" title="Supprimer" data-testid={`terminal-delete-${t.terminal_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => setShown(t)} className="text-xs text-cyan-300 border border-cyan-500/30 h-7" data-testid={`terminal-show-qr-${t.terminal_id}`}>
                  <QrCode className="w-3.5 h-3.5 mr-1" /> QR
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setKiosk(t)} className="text-xs text-[#f7c935] border border-[#f7c935]/30 h-7" data-testid={`terminal-kiosk-${t.terminal_id}`}>
                  <Tablet className="w-3.5 h-3.5 mr-1" /> Mode borne
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {shown && <QrDisplay terminal={shown} onClose={() => setShown(null)} />}
      {kiosk && <KioskDialog terminal={kiosk} api={api} onClose={() => setKiosk(null)} />}
    </div>
  );
}

function KioskDialog({ terminal, api, onClose }) {
  const [info, setInfo] = useState(null);
  const [newPin, setNewPin] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/tracking/terminals/${terminal.terminal_id}/kiosk`).then((r) => setInfo(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Chargement impossible"));
  useEffect(() => { load(); }, [terminal.terminal_id]); // eslint-disable-line
  const url = info?.url?.startsWith("http") ? info.url : `${window.location.origin}/borne/${terminal.terminal_id}`;
  const copy = (txt, label) => navigator.clipboard?.writeText(txt).then(() => toast.success(`${label} copié`)).catch(() => toast.error("Copie impossible"));
  const changePin = async () => {
    if (!/^\d{4,8}$/.test(newPin)) return toast.error("PIN de 4 à 8 chiffres");
    setBusy(true);
    try { await api.put(`/tracking/terminals/${terminal.terminal_id}/kiosk-pin`, { pin: newPin }); toast.success("PIN modifié — les tablettes devront le ressaisir"); setNewPin(""); await load(); } catch (e) { toast.error(e.response?.data?.detail || "Modification impossible"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-4" onClick={onClose} data-testid="kiosk-dialog">
      <div className="bg-[#14101c] border border-white/10 rounded-2xl p-5 max-w-md w-full text-white space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2"><Tablet className="w-5 h-5 text-[#f7c935]" /><h3 className="font-black">Mode borne — {terminal.name}</h3></div>
        <p className="text-xs text-slate-400">Ouvrez ce lien sur la tablette du site, saisissez le PIN une seule fois : la borne affiche le QR plein écran, l'horloge et les pointages du jour, sans connexion utilisateur (jeton valable {info?.token_days || 30} jours).</p>
        <div className="bg-[#0b0811] border border-white/10 rounded-lg p-3 space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Lien de la borne</div>
          <div className="flex items-center gap-2">
            <code className="text-[11px] text-cyan-300 break-all flex-1" data-testid="kiosk-url">{url}</code>
            <button onClick={() => copy(url, "Lien")} className="p-1.5 rounded bg-white/10" title="Copier" data-testid="kiosk-copy-url"><Copy className="w-3.5 h-3.5" /></button>
            <a href={url} target="_blank" rel="noreferrer" className="p-1.5 rounded bg-white/10" title="Ouvrir" data-testid="kiosk-open-url"><ExternalLink className="w-3.5 h-3.5" /></a>
          </div>
        </div>
        <div className="bg-[#0b0811] border border-white/10 rounded-lg p-3 space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">PIN de déverrouillage</div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-black tracking-[0.3em] text-[#f7c935] flex-1" data-testid="kiosk-pin">{info?.pin || "······"}</span>
            <button onClick={() => info?.pin && copy(info.pin, "PIN")} className="p-1.5 rounded bg-white/10" title="Copier" data-testid="kiosk-copy-pin"><Copy className="w-3.5 h-3.5" /></button>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Input value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 8))} placeholder="Nouveau PIN" inputMode="numeric" className="bg-[#14101c] border-white/10 text-white text-xs h-8" data-testid="kiosk-new-pin-input" />
            <Button size="sm" onClick={changePin} disabled={busy} className="bg-white/10 hover:bg-white/20 text-white text-xs h-8" data-testid="kiosk-change-pin-btn"><KeyRound className="w-3.5 h-3.5 mr-1" /> Changer</Button>
          </div>
        </div>
        <div className="flex justify-end"><Button size="sm" variant="ghost" onClick={onClose} className="text-xs text-slate-300" data-testid="kiosk-dialog-close">Fermer</Button></div>
      </div>
    </div>
  );
}

function QrDisplay({ terminal, onClose }) {
  const [tick, setTick] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  const src = `${API}/api/tracking/qr/${terminal.terminal_id}/image?v=${tick}`;
  const print = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><title>QR ${terminal.name}</title></head><body style="font-family:sans-serif;text-align:center;padding:40px"><h1>${terminal.name}</h1><img src="${src}" style="width:420px;height:420px" onload="setTimeout(()=>window.print(),300)"/><p>Scannez ce QR avec l'app Blitzz Salarié pour pointer.<br/>Ce code change toutes les 20 minutes : préférez un écran plutôt qu'une impression.</p></body></html>`);
    w.document.close();
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-4" onClick={onClose} data-testid="qr-display">
      <div className="bg-white rounded-2xl p-6 text-center max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
        <div className="text-[#0b0811] font-black text-lg">{terminal.name}</div>
        <div className="text-slate-500 text-xs mb-3">Scannez avec l'app Blitzz Salarié</div>
        <img key={tick} src={src} alt={`QR ${terminal.name}`} className="w-72 h-72 mx-auto" data-testid="qr-display-image" />
        <div className="text-[11px] text-slate-500 mt-3 flex items-center justify-center gap-1"><RefreshCw className="w-3 h-3" /> Code renouvelé automatiquement toutes les 20 min</div>
        <div className="flex gap-2 mt-4 justify-center">
          <Button size="sm" onClick={print} className="bg-[#0b0811] text-white text-xs" data-testid="qr-print-btn"><Printer className="w-3.5 h-3.5 mr-1" /> Imprimer / Plein écran</Button>
          <Button size="sm" variant="ghost" onClick={onClose} className="text-xs text-slate-600" data-testid="qr-display-close">Fermer</Button>
        </div>
      </div>
    </div>
  );
}
